import React, { useEffect, useState } from 'react';
import { useReducedMotion } from 'framer-motion';

interface TypewriterProps {
  words: string[];
  typingMs?: number;
  deletingMs?: number;
  holdMs?: number;
  className?: string;
}

const Typewriter: React.FC<TypewriterProps> = ({
  words,
  typingMs = 70,
  deletingMs = 40,
  holdMs = 1800,
  className,
}) => {
  const [text, setText] = useState('');
  const [wordIndex, setWordIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const reducedMotion = useReducedMotion();

  // One timeout per effect run, cleared on cleanup — safe under StrictMode double-mount
  useEffect(() => {
    if (reducedMotion || words.length === 0) return;
    const word = words[wordIndex % words.length];

    let timeout: ReturnType<typeof setTimeout>;
    if (!deleting) {
      timeout =
        text === word
          ? setTimeout(() => setDeleting(true), holdMs)
          : setTimeout(() => setText(word.slice(0, text.length + 1)), typingMs);
    } else {
      timeout =
        text === ''
          ? setTimeout(() => {
              setWordIndex((i) => (i + 1) % words.length);
              setDeleting(false);
            }, typingMs)
          : setTimeout(() => setText(text.slice(0, -1)), deletingMs);
    }

    return () => clearTimeout(timeout);
  }, [text, deleting, wordIndex, words, typingMs, deletingMs, holdMs, reducedMotion]);

  return (
    <span className={className}>
      {reducedMotion ? words[0] : text}
      <span aria-hidden="true" className="motion-safe:animate-blink">
        |
      </span>
    </span>
  );
};

export default Typewriter;
